import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { MemoEditor } from '../components/MemoEditor';
import { MemoCard } from '../components/MemoCard';
import { DateNavigator } from '../components/DateNavigator';
import { MemoTypeFilter } from '../components/MemoTypeFilter';
import { MemoEditorProvider, useMemoEditor } from '../context/MemoEditorContext';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';

interface MemosApiResponse {
    data: any[];
}

function HomeContent() {
    const [searchParams, setSearchParams] = useSearchParams();

    // Get selected date from URL or default to today
    const selectedDate = useMemo(() => {
        const dateParam = searchParams.get('date');
        if (dateParam) return dateParam;
        return format(new Date(), 'yyyy-MM-dd');
    }, [searchParams]);

    // Get memo type filter from URL
    const memoTypeFilter = searchParams.get('memo_type') || null;
    const entityFilter = searchParams.get('entity') || null;

    // Derived filters from URL
    const category = searchParams.get('category') || 'MEMO';
    const type = searchParams.get('type') || undefined;

    // Build filters based on whether type or entity filter is active
    const filters = useMemo(() => {
        const baseFilters: any = {};
        if (memoTypeFilter) baseFilters.type = memoTypeFilter;
        if (entityFilter) {
            baseFilters.entity_code = entityFilter;
        }

        if (Object.keys(baseFilters).length > 0) {
            return baseFilters;
        }

        // Default: show memos for selected date with category filter
        return {
            category,
            type,
            start_date: selectedDate,
            end_date: selectedDate,
        };
    }, [memoTypeFilter, entityFilter, category, type, selectedDate]);

    const {
        data,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['memos', filters],
        queryFn: async () => {
            const res = await api.getMemos({
                ...filters
            });
            return res as MemosApiResponse;
        },
    });

    const handleDateChange = (date: string) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('date', date);
        // Clear filters when changing date
        newParams.delete('memo_type');
        newParams.delete('entity');
        setSearchParams(newParams);
    };

    const handleMemoTypeChange = (memoType: string | null) => {
        const newParams = new URLSearchParams(searchParams);
        if (memoType) {
            newParams.set('memo_type', memoType);
        } else {
            newParams.delete('memo_type');
        }
        setSearchParams(newParams);
    };

    const handleEntityChange = (entity: string | null) => {
        const newParams = new URLSearchParams(searchParams);
        if (entity) {
            newParams.set('entity', entity);
        } else {
            newParams.delete('entity');
        }
        setSearchParams(newParams);
    };

    const handleTodayClick = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('date', format(new Date(), 'yyyy-MM-dd'));
        // Clear filters when clicking Today
        newParams.delete('memo_type');
        newParams.delete('entity');
        setSearchParams(newParams);
    };

    const { openEditor } = useMemoEditor();

    // Backend handles entity filtering, so we use the response directly
    const displayedMemos = data?.data || [];

    if (isLoading) return <div className="flex justify-center p-8">Loading...</div>;
    if (error) return <div className="text-red-500 p-8">Error loading memos</div>;

    return (
        <div className="flex flex-col h-[calc(100vh-4rem)]">
            {/* Date Navigator with Filter and FAB */}
            <DateNavigator
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                onTodayClick={handleTodayClick}
                disabled={!!memoTypeFilter || !!entityFilter}
                filterSlot={
                    <MemoTypeFilter
                        selectedType={memoTypeFilter}
                        onTypeChange={handleMemoTypeChange}
                        selectedEntity={entityFilter}
                        onEntityChange={handleEntityChange}
                    />
                }
            >
                <Button
                    onClick={() => openEditor({ date: new Date(selectedDate) })}
                    size="icon"
                    className="h-12 w-12 sm:h-14 sm:w-14 rounded-full shadow-lg shadow-zinc-900/10 dark:shadow-zinc-950/50 hover:shadow-xl hover:shadow-zinc-900/15 dark:hover:shadow-zinc-950/60 hover:scale-105 transition-all"
                    aria-label="New memo"
                >
                    <Plus className="h-5 w-5 sm:h-6 sm:w-6" />
                </Button>
            </DateNavigator>

            {/* Memo List */}
            <div className="flex-1 overflow-y-auto px-4 py-2 pb-28 no-scrollbar">
                <div className="max-w-2xl mx-auto">
                    <div className="space-y-1">
                        {displayedMemos.map((memo: any) => (
                            <MemoCard key={memo.memo_id} memo={memo} />
                        ))}
                    </div>

                    {displayedMemos.length === 0 && !isLoading && (
                        <div className="text-center text-zinc-500 py-10">
                            {memoTypeFilter || entityFilter
                                ? `No memos found for current filters.`
                                : 'No memos for this date. Start writing!'
                            }
                        </div>
                    )}
                </div>
            </div>

            <MemoEditor />
        </div>
    );
}

export function Home() {
    return (
        <MemoEditorProvider>
            <HomeContent />
        </MemoEditorProvider>
    );
}
